import type {ReactElement} from 'react';
import {useState} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {handlePublishRequest} from '@Panels/poster-publishing-options-panel/components/publishing-as-webpage-panel/publishing-as-webpage-library';
import {PublishingAsWebpageType} from '@Panels/poster-publishing-options-panel/components/publishing-as-webpage-panel/publishing-as-webpage-panel.types';
import {useAppDispatch} from '@/hooks';
import {updatePublishDropDownState} from '@Components/poster-editor/poster-editor-reducer';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {openNewPublishDialog} from '@Libraries/open-new-publish-dialog';

enum PublishOptions {
  COPY = 'copy',
  POST_TO_SOCIAL = 'post_to_social',
  SEND_AN_EMAIL = 'send_as_email',
  CREATE_EVENT = 'create_event',
  MORE_OPTIONS = 'more_options',
}

interface DownloadPanelPublishingSectionProps {
  isOpenedInModal?: boolean;
}

export function DownloadPanelPublishingSection({isOpenedInModal = false}: DownloadPanelPublishingSectionProps): ReactElement {
  const [isWebLinkLoading, setIsWebLinkLoading] = useState(false);
  const dispatch = useAppDispatch();
  const {posterObject} = useDownloadPanelData();
  const getIconForOption = (icon: string, isLoading = false): ReactElement => {
    return (
      <Icon
        icon={icon}
        className="_cursor-pointer"
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.CIRCLE}
        type={IconType.FLAT}
        isLoading={isLoading}
        flatIconType={{
          color: FlatIconColor.PRIMARY,
          type: FlatIconColorType.DEFAULT,
        }}
      />
    );
  };

  const copyLink = async (link: string): Promise<void> => {
    try {
      setIsWebLinkLoading(true);
      await handlePublishRequest(PublishingAsWebpageType.PUBLISH_POSTER, posterObject.hashedID);
      setIsWebLinkLoading(false);
      await navigator.clipboard.writeText(link);
      showMessageGrowl({
        key: 'copy-poster-link-success',
        text: window.i18next.t('pmwjs_copied'),
        type: GROWL_TYPE.SUCCESS,
        interval: 3000,
      });
    } catch (err) {
      setIsWebLinkLoading(false);
      console.error(err);
    }
  };

  const onClickHandler = (option: PublishOptions): void => {
    trackPosterBuilderGA4Events(GA4EventName.PUBLISH_NOW_DOWNLOAD_DIALOG, {[GA4EventParamName.TYPE]: option});
    switch (option) {
      case PublishOptions.COPY:
        void copyLink(window.PMW.util.site_url(`posterbuilder/view/${posterObject.hashedID}`));
        break;
      case PublishOptions.POST_TO_SOCIAL:
        window.open(window.PMW.util.site_url(`socialmedia/post/design/${posterObject.hashedID}`), '_blank');
        break;
      case PublishOptions.SEND_AN_EMAIL:
        window.open(window.PMW.util.site_url(`emailmarketing/publish/design/${posterObject.hashedID}`), '_blank');
        break;
      case PublishOptions.CREATE_EVENT:
        window.open(window.PMW.util.site_url(`event/wizard?st=3&tcph=${posterObject.hashedID}&tcps=1`), '_blank');
        break;
      case PublishOptions.MORE_OPTIONS:
        if (isEditorMobileVariant()) {
          openNewPublishDialog(posterObject.hashedID);
        } else {
          dispatch(updatePublishDropDownState(true));
        }
        break;
      default:
        break;
    }
  };

  const getTextForOption = (text: string, className = ''): ReactElement => {
    return <Text size={TextSize.XXSMALL} val={text} className={`content-body text-center ${className}`} />;
  };
  const preparePublishOptions = (option: PublishOptions): ReactElement => {
    switch (option) {
      case PublishOptions.COPY:
        return (
          <>
            {getIconForOption('icon-link', isWebLinkLoading)}
            {getTextForOption(window.i18next.t('pmwjs_copy_link'), 'spacing-p-l-3 spacing-p-r-3')}
          </>
        );
      case PublishOptions.POST_TO_SOCIAL:
        return (
          <>
            {getIconForOption('icon-social-media')}
            {getTextForOption(window.i18next.t('pmwjs_post_to_socials'))}
          </>
        );
      case PublishOptions.SEND_AN_EMAIL:
        return (
          <>
            {getIconForOption('icon-envelope')}
            {getTextForOption(window.i18next.t('pmwjs_send_as_email'))}
          </>
        );
      case PublishOptions.CREATE_EVENT:
        return (
          <>
            {getIconForOption('icon-event')}
            {getTextForOption(window.i18next.t('pmwjs_create_event'))}
          </>
        );
      default:
        return (
          <>
            {getIconForOption('icon-more-h')}
            {getTextForOption(window.i18next.t('pmwjs_more_options'))}
          </>
        );
    }
  };

  return (
    <div
      className={`${!isOpenedInModal ? 'spacing-p-l-4 spacing-p-r-4 spacing-p-t-3 spacing-p-b-4 radius-12 shadow-floating-elevation-3 border-s-standard' : 'spacing-p-0'} flex-v-row gap-4`}
    >
      <Text bold className="content-body" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_looking_to_publish_your_design')} />
      <ul className={`flex-h-row justify-content-between ${isOpenedInModal ? 'gap-4' : ''}`}>
        <li
          onClick={(): void => {
            onClickHandler(PublishOptions.POST_TO_SOCIAL);
          }}
          className="flex-v-row gap-2 _cursor-pointer _full-width flex-items-center justify-content-center"
        >
          {preparePublishOptions(PublishOptions.POST_TO_SOCIAL)}
        </li>
        <li
          onClick={(): void => {
            onClickHandler(PublishOptions.SEND_AN_EMAIL);
          }}
          className="flex-v-row gap-2 _cursor-pointer _full-width flex-items-center justify-content-center"
        >
          {preparePublishOptions(PublishOptions.SEND_AN_EMAIL)}
        </li>
        <li
          onClick={(): void => {
            onClickHandler(PublishOptions.CREATE_EVENT);
          }}
          className="flex-v-row gap-2 _cursor-pointer _full-width flex-items-center justify-content-center"
        >
          {preparePublishOptions(PublishOptions.CREATE_EVENT)}
        </li>
        <li
          onClick={(): void => {
            onClickHandler(PublishOptions.COPY);
          }}
          className="flex-v-row gap-2 _cursor-pointer _full-width flex-items-center justify-content-center"
        >
          {preparePublishOptions(PublishOptions.COPY)}
        </li>
        <li
          onClick={(): void => {
            onClickHandler(PublishOptions.MORE_OPTIONS);
          }}
          className="flex-v-row gap-2 _cursor-pointer _full-width flex-items-center justify-content-center"
        >
          {preparePublishOptions(PublishOptions.MORE_OPTIONS)}
        </li>
      </ul>
    </div>
  );
}
